import { footprintOnPlane } from "./vxGeom";

/**
 * Sutherland-Hodgman: clip convex polygon `poly` by the half-plane on the
 * inside of convex polygon `clipPoly` (one edge at a time). Both polygons
 * must be wound consistently (either both CW or both CCW); footprints from
 * detectorCorners/footprintOnPlane are consistently wound since they come
 * from a fixed corner order projected through a single point.
 */
function clipPolygon(poly, clipPoly) {
  let output = poly;
  const n = clipPoly.length;

  for (let i = 0; i < n && output.length > 0; i++) {
    const a = clipPoly[i];
    const b = clipPoly[(i + 1) % n];
    const edgeX = b[0] - a[0];
    const edgeY = b[1] - a[1];
    // Inside test uses the sign convention of the polygon's own winding,
    // determined once from the clip polygon's signed area.
    const input = output;
    output = [];
    for (let j = 0; j < input.length; j++) {
      const cur = input[j];
      const prev = input[(j - 1 + input.length) % input.length];
      const curInside = (edgeX * (cur[1] - a[1]) - edgeY * (cur[0] - a[0])) * clipSign >= 0;
      const prevInside = (edgeX * (prev[1] - a[1]) - edgeY * (prev[0] - a[0])) * clipSign >= 0;
      if (curInside) {
        if (!prevInside) {
          output.push(intersect(prev, cur, a, b));
        }
        output.push(cur);
      } else if (prevInside) {
        output.push(intersect(prev, cur, a, b));
      }
    }
  }
  return output;
}

function intersect(p1, p2, a, b) {
  const x1 = p1[0], y1 = p1[1], x2 = p2[0], y2 = p2[1];
  const x3 = a[0], y3 = a[1], x4 = b[0], y4 = b[1];
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-15) return p2;
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
}

function signedArea(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}

let clipSign = 1;

/** Exact intersection polygon of a set of convex polygons (Sutherland-Hodgman). */
export function intersectConvexPolygons(polys) {
  if (polys.length === 0) return [];
  let result = polys[0];
  for (let i = 1; i < polys.length && result.length > 0; i++) {
    const clipPoly = polys[i];
    clipSign = signedArea(clipPoly) >= 0 ? 1 : -1;
    result = clipPolygon(result, clipPoly);
  }
  return result;
}

export function polygonArea(poly) {
  return Math.abs(signedArea(poly));
}

/**
 * Exact common-FOV intersection of all per-angle footprints on plane z=zPlane.
 * Returns { area, footprints, outline } where outline is the exact
 * intersection polygon (empty array if the footprints don't all overlap).
 */
export function commonFovAtZ(vecs, halfU, halfV, zPlane) {
  const footprints = [];
  for (const vec of vecs) {
    const poly = footprintOnPlane(vec, halfU, halfV, zPlane);
    if (poly === null) return { area: 0, footprints: [], outline: [] };
    footprints.push(poly);
  }

  const outline = intersectConvexPolygons(footprints);
  const area = outline.length >= 3 ? polygonArea(outline) : 0;

  return { area, footprints, outline };
}

/** Rough FOV scale estimate so the 2D canvas window fits the geometry. */
export function estimateGridHalf(sod, sdd, detWidthPx, detPitch) {
  return Math.max((detPitch * detWidthPx * sod) / sdd * 1.05, 1e-4);
}

export function eqDiameter(area) {
  return area > 0 ? 2 * Math.sqrt(area / Math.PI) : 0;
}
